import React from "react";
import { motion } from "framer-motion";

/**
 * OnboardingHero — big centered logo panel above onboarding forms.
 *
 * Used by:
 *   - /accept-invite/:token  (auth invite acceptance)
 *   - /verify-id/:token      (Applicant ID verification)
 *   - /join                  (Recruiting application)
 *
 * The `variant` prop controls background:
 *   "dark"  — for standalone pages sitting on the navy gradient (invite / verify-id)
 *   "light" — for pages already inside the site chrome (recruiting)
 *
 * NOTE: reuses /brand/logo.png — same asset as the marketing header logo,
 * just displayed at hero size. Do NOT introduce a second copy.
 */
export default function OnboardingHero({
    variant = "dark",
    eyebrow,
    title,
    subtitle,
}) {
    const isDark = variant === "dark";
    return (
        <motion.header
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className={`flex flex-col items-center text-center ${
                isDark ? "py-10 sm:py-12" : "py-14 sm:py-16"
            }`}
            data-testid="onboarding-hero"
        >
            <div
                className={`inline-flex items-center justify-center rounded-3xl p-4 sm:p-5 shadow-xl ${
                    isDark
                        ? "bg-white/95 ring-1 ring-white/40 backdrop-blur"
                        : "bg-white ring-1 ring-mm-line"
                }`}
                data-testid="onboarding-hero-logo-wrap"
            >
                <img
                    src="/brand/logo.png"
                    alt="Moving Mountains Advisors"
                    className="h-24 w-24 object-contain sm:h-32 sm:w-32 lg:h-36 lg:w-36"
                    data-testid="onboarding-hero-logo-img"
                    draggable={false}
                />
            </div>

            <div
                className={`mt-5 text-[10px] font-semibold uppercase tracking-[0.32em] ${
                    isDark ? "text-white/75" : "text-mm-muted"
                }`}
                data-testid="onboarding-hero-wordmark"
            >
                Moving Mountains Advisors
            </div>

            {eyebrow && (
                <div
                    className={`mt-6 text-[11px] font-semibold uppercase tracking-[0.26em] ${
                        isDark ? "text-mm-sky" : "text-mm-blue"
                    }`}
                    data-testid="onboarding-hero-eyebrow"
                >
                    {eyebrow}
                </div>
            )}

            {title && (
                <h1
                    className={`mt-2 mm-display text-3xl sm:text-4xl lg:text-5xl ${
                        isDark ? "text-white" : "text-mm-navy"
                    }`}
                    data-testid="onboarding-hero-title"
                >
                    {title}
                </h1>
            )}

            {subtitle && (
                <p
                    className={`mt-3 max-w-xl text-sm sm:text-base leading-relaxed ${
                        isDark ? "text-white/70" : "text-mm-muted"
                    }`}
                    data-testid="onboarding-hero-subtitle"
                >
                    {subtitle}
                </p>
            )}
        </motion.header>
    );
}
